import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';

import { SemantriaAccount } from 'app/providers/bridge-api/account.service';
import {
  FieldStatisticsPayload,
  FieldStatisticsService,
} from 'app/providers/field-statistics.service';
import { CalculationRequest } from 'app/providers/storage-api/predictive.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { TimezoneService } from 'app/services/timezone.service';
import { AlertService } from '../../../../services/alert.service';
import { WidgetDriverAnalysisCreateOptions } from '../project-collection-actions/project-collection-actions.component';
import { ProjectCollection } from '../../models/project-collection.model';
import { AnalysisNameStepComponent } from './analysis-name-step/analysis-name-step.component';
import { AnalysisTargetStepComponent } from './analysis-target-step/analysis-target-step.component';
import { DriverQuestionsStepComponent } from './driver-questions-step/driver-questions-step.component';
import { HierarchyStepComponent } from './hierarchy-step/hierarchy-step.component';
import { WeightStepComponent } from './weight-step/weight-step.component';

export interface DriverAnalysisStep {
  data: Partial<CalculationRequest>;
  collection: ProjectCollection;
  fieldStatisticsPayload: FieldStatisticsPayload;
  isLoading: boolean;
  widgetData?: WidgetDriverAnalysisCreateOptions;

  onValidityChange: EventEmitter<boolean>;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-driver-analysis-dialog',
  templateUrl: './driver-analysis-dialog.component.html',
  styleUrls: ['./driver-analysis-dialog.component.scss'],
  standalone: false,
})
export class DriverAnalysisDialogComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() data: { collection: ProjectCollection; widgetData: WidgetDriverAnalysisCreateOptions };

  @Output() modalClosed: EventEmitter<any> = new EventEmitter();

  @ViewChild('analysisStep', { static: true, read: ViewContainerRef })
  analysisStep: ViewContainerRef;

  formStateOrder: Type<DriverAnalysisStep>[] = [
    AnalysisNameStepComponent,
    AnalysisTargetStepComponent,
    DriverQuestionsStepComponent,
    WeightStepComponent,
    HierarchyStepComponent,
  ];
  formState: Partial<CalculationRequest> = {
    displayName: '',
    properties: {},
  };
  currentStepIdx = -1;
  isFormStateValid;
  fieldStatisticsPayload: FieldStatisticsPayload;
  isLoading;

  validitySub$: Subscription;

  constructor(
    private fieldStatisticsService: FieldStatisticsService,
    private semantriaAccount: SemantriaAccount,
    private alertService: AlertService,
    private timezoneService: TimezoneService,
    private translocoService: TranslocoService
  ) {}

  public async ngOnInit() {
    this.formState.displayName = this.data.collection.data.displayName;
    this.formState.tags = ['fromWidget'];
    this.formState.properties.query = this.data.widgetData.query;
    this.formState.properties.analysisId = this.data.widgetData.analysisId;
    this.formState.properties['dashboard:filters'] = JSON.stringify(this.data.widgetData.filters);
    this.formState.properties.userSearch = this.data.widgetData.userSearch
      ? JSON.stringify(this.data.widgetData.userSearch)
      : undefined;
    if (this.formState.properties.query) {
      this.formState.properties.timezone = this.timezoneService.timezone;
    }
    if (this.data.widgetData.widgetGroupId) {
      this.formState.properties['dashboard:widgetGroupId'] = this.data.widgetData.widgetGroupId;
      if (this.data.widgetData.widgetGroupFilters) {
        this.formState.properties['dashboard:widgetGroupFilters'] = JSON.stringify(
          this.data.widgetData.widgetGroupFilters
        );
      }
    }
    if (this.data.widgetData.comparisonId) {
      this.formState.properties['dashboard:comparisonId'] = this.data.widgetData.comparisonId;
      if (this.data.widgetData.comparisonFilters) {
        this.formState.properties['dashboard:comparisonFilters'] = JSON.stringify(
          this.data.widgetData.comparisonFilters
        );
      }
    }
    this.fieldStatisticsPayload = this.data.widgetData.fieldStatisticPayload;

    await this.nextStep();

    // we'll need this later so lets start loading them now
    this.semantriaAccount.sub.load();
  }

  public async previousStep() {
    await this.createStep(--this.currentStepIdx);
  }

  public async nextStep() {
    await this.createStep(++this.currentStepIdx);
  }

  private async fetchFieldStatistics() {
    if (
      this.fieldStatisticsPayload &&
      this.fieldStatisticsPayload.mustExist === this.formState.dependentColumn
    ) {
      return;
    }
    // We keep this to refetch despite we have these available in case of mustExists
    const customFields =
      this.data.collection.analyses[0].unAliasedCustomFields?.join() ||
      this.data.collection.analyses[0].customFields?.join() ||
      '*';
    this.fieldStatisticsPayload = await this.fieldStatisticsService.getStatsForCollection(
      this.data.collection.data.projectId,
      this.data.collection.data.collectionName,
      undefined,
      'xx', // std fields not needed
      customFields,
      this.formState.dependentColumn,
      this.data.widgetData?.query,
      this.timezoneService.timezone
    );
  }

  private async createStep(currentStep) {
    this.analysisStep.clear();

    try {
      this.isLoading = true;
      await this.fetchFieldStatistics();
    } catch (error) {
      this.alertService.showErrorToast(
        this.translocoService.translate('driver-analysis-dialog.failed-to-fetch'),
        error
      );
      return;
    } finally {
      this.isLoading = false;
    }

    const componentRef = this.analysisStep.createComponent<DriverAnalysisStep>(
      this.formStateOrder[currentStep]
    );
    componentRef.instance.data = this.formState;
    componentRef.instance.collection = this.data.collection;
    componentRef.instance.fieldStatisticsPayload = this.fieldStatisticsPayload;
    if (this.data.widgetData) {
      componentRef.instance.widgetData = this.data.widgetData;
    }

    if (this.validitySub$) {
      this.validitySub$.unsubscribe();
    }
    this.validitySub$ = componentRef.instance.onValidityChange.subscribe(
      (res) => (this.isFormStateValid = res)
    );
  }

  public get isCurrentStateValid() {
    return this.isFormStateValid;
  }

  public cancel() {
    this.modalClosed.emit();
  }

  public analyze() {
    if (this.formState.breakdownColumn === 'No Hierarchy') {
      this.formState.breakdownColumn = null;
    }

    this.modalClosed.emit(this.formState);
  }
}
